import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
// evertel
import { Button, InfoBox, ModalHeader, Modal, ModalBody, Row, Col, Input, ModalFooter, Label } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { CurrentUserController } from '@evertel/blue-user'


interface JoinRoomRequestModalProps {
    visible: boolean
    onClose: () => void
    onSave: (body: any) => void
}

const JoinRoomRequestModal: React.FC<JoinRoomRequestModalProps> = observer(({
    visible,
    onClose,
    onSave
}) => {

    const analytics = useService(Analytics, [])
    const currentUserController = useService(CurrentUserController, [])

    const [body, setBody] = useState({
        department: '',
        location: '',
        position: '',
        message: ''
    })
    const [error, setError] = useState(null)

    useEffect(() => {
        if (visible) {
            setError(null)

            analytics.logView('public-room-request-extra-info', true)

            // Pre-populate with user's department info if available
            const primaryDepartment = currentUserController.primaryDepartment
            const primaryDepartmentAccess = currentUserController.primaryDepartmentsAccess

            setBody(prevState => ({
                department: primaryDepartment?.name || '',
                location: [primaryDepartment?.city, primaryDepartment?.state]
                    .filter(Boolean).join(', '),
                position: primaryDepartmentAccess?.positionString || '',
                message: prevState.message // Preserve message until submitted
            }))
        }
    }, [visible, currentUserController.primaryDepartment, currentUserController.primaryDepartmentsAccess])

    const onSubmit = () => {
        if (!body.department?.trim() || !body.location?.trim() || !body.position?.trim() || !body.message?.trim()) {
            setError('Please fill out all fields')
            return
        }

        if (onSave) onSave({
            department: body.department.trim(),
            location: body.location.trim(),
            position: body.position.trim(),
            message: body.message.trim()
        })

        setBody(prevState => ({ ...prevState, message: '' }))
        onClose()
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}>
            <ModalHeader
                title="Additional info is needed to request access to this Incident Command room"
            />
            <ModalBody>
                {(error) &&
                    <InfoBox
                        color="danger"
                        className="my-2">
                        {error}
                    </InfoBox>
                }
                <Row className="my-2">
                    <Col>
                        <Label
                            text="Your Agency"
                            htmlFor="department-input-text"
                        />
                        <Input
                            id="department-input-text"
                            name="department"
                            placeholder="Agency's name..."
                            value={body.department}
                            onChange={(e) => setBody((prevState) => ({
                                ...prevState,
                                department: e.target.value
                            }))}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <Label
                            text="Your Agency's Location"
                            htmlFor="location-input-text"
                        />
                        <Input
                            id="location-input-text"
                            name="location"
                            placeholder="Agency's city and state..."
                            value={body.location}
                            onChange={(e) => setBody((prevState) => ({ ...prevState, location: e.target.value }))}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <Label
                            text="Your Position/Title"
                            htmlFor="position-input-text"
                        />
                        <Input
                            id="position-input-text"
                            name="position"
                            placeholder="Your position/title..."
                            value={body.position}
                            onChange={(e) => setBody((prevState) => ({
                                ...prevState,
                                position: e.target.value
                            }))}
                        />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <Label
                            text="Reason Requesting Access?"
                            htmlFor="message-input-textarea"
                        />
                        <Input
                            id="message-input-textarea"
                            name="message"
                            type="textarea"
                            placeholder="Why you are requesting to join this room..."
                            value={body.message}
                            onChange={(e) => setBody((prevState) => ({
                                ...prevState,
                                message: e.target.value
                            }))}
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter align="right">
                <Button
                    color="link"
                    outline
                    onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={onSubmit}>
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    )
})

export { JoinRoomRequestModal }
